<template>
  <div
    ref="sizer"
    v-loading="loading"
    class="v-table-v2-wrap"
    :class="wrapClass"
    :style="wrapStyle"
  >
    <ep-table-v2
      v-if="tableWidth > 0 && tableHeight > 0"
      :columns="v2Columns"
      :data="displayData"
      :width="tableWidth"
      :height="tableHeight"
      :row-height="rowHeight"
      :header-height="44"
      :row-key="resolvedRowKey"
      :fixed="hasFrozenColumns"
      :sort-by="sortBy"
      @column-sort="onColumnSort"
    >
      <template #cell="{ column, rowData, rowIndex }">
        <div v-if="column.slotName" class="v2-cell v2-cell--slot">
          <slot
            :name="column.slotName"
            :record="rowData"
            :row="rowData"
            :row-index="rowIndex"
          >
            {{ cellText(rowData, column) }}
          </slot>
        </div>
        <div v-else-if="column.render" class="v2-cell v2-cell--slot">
          <render-cell
            :render="column.render"
            :record="rowData"
            :row-index="rowIndex"
          />
        </div>
        <el-tooltip
          v-else
          :disabled="!showCellTooltip(column, rowData)"
          :content="cellText(rowData, column)"
          placement="top"
          :show-after="200"
          :hide-after="0"
          popper-class="v2-cell-tooltip"
        >
          <div
            class="v2-cell"
            :class="{ 'is-ellipsis': column.ellipsis || column.tooltip }"
          >
            {{ cellText(rowData, column) }}
          </div>
        </el-tooltip>
      </template>
    </ep-table-v2>
  </div>
</template>

<script>
import { ElTableV2 } from "element-plus";

const RenderCell = {
  name: "RenderCell",
  props: {
    render: { type: Function, required: true },
    record: { type: Object, default: () => ({}) },
    rowIndex: { type: Number, default: 0 }
  },
  setup(props) {
    return () => props.render({
      record: props.record,
      rowIndex: props.rowIndex,
      row: props.record
    });
  }
};

export default {
  name: "VTableV2",
  components: { RenderCell, EpTableV2: ElTableV2 },
  inheritAttrs: false,
  props: {
    data: {
      type: Array,
      default: () => []
    },
    columns: {
      type: Array,
      default: () => []
    },
    loading: {
      type: Boolean,
      default: false
    },
    rowKey: {
      type: [String, Number],
      default: "id"
    }
  },
  emits: ["column-sort", "sorter-change"],
  data() {
    return {
      sortBy: { key: "", order: "asc" },
      tableWidth: 0,
      tableHeight: 0,
      resizeObserver: null,
      resizeRaf: 0
    };
  },
  computed: {
    resolvedRowKey() {
      return this.$attrs.rowKey || this.$attrs["row-key"] || this.rowKey || "id";
    },
    wrapClass() {
      return this.$attrs.class;
    },
    wrapStyle() {
      return { width: "100%", height: "100%", minHeight: "240px" };
    },
    rowHeight() {
      const cls = this.$attrs.class;
      const className = Array.isArray(cls) ? cls.join(" ") : String(cls || "");
      if (className.includes("customizeHeight90")) return 90;
      if (className.includes("customizeHeight62")) return 62;
      return 50;
    },
    hasFrozenColumns() {
      return (this.columns || []).some((item) => item.fixed);
    },
    v2Columns() {
      const list = this.columns || [];
      const tableWidth = Math.max((this.tableWidth || 0) - 8, 0);
      const parsed = list.map((item) => {
        const hasWidth = item.width != null && item.width !== "";
        const fixed = item.fixed === "right" ? "right" : item.fixed ? true : undefined;
        return {
          item,
          hasWidth,
          fixed,
          width: hasWidth ? Number(item.width) : 0
        };
      });
      const used = parsed.reduce((sum, col) => sum + (col.hasWidth ? col.width : 0), 0);
      const unsetCount = parsed.filter((col) => !col.hasWidth).length;
      const unsetWidth = unsetCount
        ? Math.max(Math.floor((tableWidth > used ? tableWidth - used : 0) / unsetCount), 80)
        : 120;
      const cols = parsed.map(({ item, hasWidth, fixed, width }) => {
        const dataKey = item.dataIndex || item.dataIndexs || item.key || item.slotName;
        const colWidth = hasWidth ? width : unsetWidth;
        return {
          ...item,
          key: String(item.key || dataKey || item.title),
          dataKey,
          title: item.title,
          width: colWidth,
          minWidth: Number(item.minWidth || (hasWidth ? width : 80)),
          flexGrow: fixed ? 0 : 1,
          flexShrink: 1,
          fixed,
          sortable: Boolean(!item.fixed && item.sortable),
          slotName: item.slotName,
          align: item.align
        };
      });
      const unfixed = cols.filter((col) => !col.fixed);
      const frozenWidth = cols
        .filter((col) => col.fixed)
        .reduce((sum, col) => sum + Number(col.width || 0), 0);
      const currentUnfixed = unfixed.reduce((sum, col) => sum + Number(col.width || 0), 0);
      const available = tableWidth > frozenWidth ? tableWidth - frozenWidth : currentUnfixed;
      if (unfixed.length && available > 0 && currentUnfixed > 0) {
        let assigned = 0;
        unfixed.forEach((col, index) => {
          if (index === unfixed.length - 1) {
            col.width = Math.max(available - assigned, Number(col.minWidth || 80));
          } else {
            col.width = Math.max(
              Math.floor((Number(col.width) / currentUnfixed) * available),
              Number(col.minWidth || 80)
            );
            assigned += col.width;
          }
        });
      }
      return cols;
    },
    displayData() {
      const rows = [...(this.data || [])];
      const { key, order } = this.sortBy || {};
      const column = (this.columns || []).find(
        (item) => (item.dataIndex || item.dataIndexs || item.key) === key
      );
      if (!key || !order || !column) return rows;
      if (column.sortable && typeof column.sortable === "object" && column.sortable.sorter === true) {
        return rows;
      }
      rows.sort((a, b) => {
        if (typeof column.sortMethod === "function") {
          const cmp = column.sortMethod(a, b);
          return order === "desc" ? -cmp : cmp;
        }
        const va = a?.[key];
        const vb = b?.[key];
        const na = Number(va);
        const nb = Number(vb);
        let cmp = 0;
        if (va !== "" && vb !== "" && !Number.isNaN(na) && !Number.isNaN(nb)) {
          cmp = na - nb;
        } else {
          cmp = String(va ?? "").localeCompare(String(vb ?? ""), "zh-CN");
        }
        return order === "desc" ? -cmp : cmp;
      });
      return rows;
    }
  },
  mounted() {
    this.measureSize();
    if (typeof ResizeObserver !== "undefined") {
      this.resizeObserver = new ResizeObserver(() => this.scheduleMeasure());
      this.resizeObserver.observe(this.$refs.sizer);
    }
    window.addEventListener("resize", this.scheduleMeasure);
  },
  beforeUnmount() {
    window.removeEventListener("resize", this.scheduleMeasure);
    if (this.resizeRaf) cancelAnimationFrame(this.resizeRaf);
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
  },
  methods: {
    formatCell(row, dataKey) {
      if (!dataKey || !row) return "";
      return dataKey.split(".").reduce((value, key) => {
        if (value && typeof value === "object") return value[key];
        return undefined;
      }, row);
    },
    cellText(row, column) {
      const value = this.formatCell(row, column && column.dataKey);
      if (value == null || value === "") return "";
      if (Array.isArray(value)) return value.filter((item) => item != null && item !== "").join("，");
      return String(value);
    },
    showCellTooltip(column, row) {
      if (!column || !(column.ellipsis || column.tooltip)) return false;
      return Boolean(this.cellText(row, column));
    },
    onColumnSort(payload) {
      this.sortBy = { key: payload.key, order: payload.order };
      this.$emit("column-sort", payload);
      this.$emit("sorter-change", payload);
    },
    measureSize() {
      const el = this.$refs.sizer;
      if (!el) return;
      const width = Math.floor(el.clientWidth);
      const height = Math.floor(el.clientHeight);
      if (width <= 0 || height <= 0) return;
      if (Math.abs(width - this.tableWidth) < 2 && Math.abs(height - this.tableHeight) < 2) {
        return;
      }
      this.tableWidth = width;
      this.tableHeight = height;
    },
    scheduleMeasure() {
      if (this.resizeRaf) cancelAnimationFrame(this.resizeRaf);
      this.resizeRaf = requestAnimationFrame(() => {
        this.resizeRaf = 0;
        this.measureSize();
      });
    }
  }
};
</script>

<style scoped>
.v-table-v2-wrap {
  min-height: 240px;
  overflow: hidden;
}

.v-table-v2-wrap :deep(.el-table-v2) {
  width: 100%;
}

.v-table-v2-wrap :deep(.el-table-v2__row) {
  overflow: hidden;
}

.v-table-v2-wrap :deep(.el-table-v2__row-cell),
.v-table-v2-wrap :deep(.el-table-v2__header-cell) {
  overflow: hidden;
  min-width: 0;
}

.v-table-v2-wrap :deep(.el-table-v2__row-cell) {
  display: flex;
  align-items: center;
}

.v-table-v2-wrap :deep(.v2-cell.is-ellipsis > .el-tooltip__trigger),
.v-table-v2-wrap :deep(.v2-cell.is-ellipsis > .el-only-child__content) {
  display: block;
  width: 100%;
  min-width: 0;
  overflow: hidden;
}

.v-table-v2-wrap :deep(.el-table-v2__cell-text) {
  display: block;
  width: 100%;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.v2-cell {
  display: block;
  width: 100%;
  min-width: 0;
  overflow: hidden;
}

.v2-cell--slot {
  white-space: normal;
  text-overflow: unset;
}

.v2-cell.is-ellipsis {
  white-space: nowrap;
  text-overflow: ellipsis;
}

.v2-cell.is-ellipsis :deep(img),
.v2-cell--slot :deep(img) {
  display: block;
  max-width: 100%;
  white-space: normal;
  text-overflow: unset;
}
</style>

<style>
.v2-cell-tooltip {
  max-width: 480px !important;
  line-height: 1.5;
  white-space: pre-wrap;
  word-break: break-word;
}
</style>
